<template>
  <div class="h-full">
    <!-- Overlay (drawer only) -->
    <slot name="overlay" />

    <!-- Main container -->
    <div :class="containerClasses" :style="containerStyle">
      <!-- Header -->
      <div
        class="p-5 border-b border-gray-100 flex items-center justify-between bg-white shrink-0"
      >
        <div class="flex items-center gap-3">
          <h2 class="text-xl font-serif font-bold flex items-center gap-2">
            <ShoppingBag :size="20" /> Your Order
          </h2>
        </div>
        <div class="flex items-center gap-2">
          <!-- Pin/Unpin Button -->
          <Button
            v-if="showPinButton"
            @click.stop.prevent="handlePinAction"
            :icon="pinButtonType === 'pin' ? Pin : PinOff"
            :icon-size="20"
            variant="ghost"
            :custom-class="
              pinButtonType === 'pin'
                ? 'hidden lg:flex p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500'
                : 'p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500'
            "
            circular
            size="sm"
          />
          <!-- Close Button -->
          <Button
            v-if="showCloseButton"
            @click="$emit('close')"
            custom-class="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500"
            :icon="X"
            :icon-size="24"
            size="sm"
            variant="ghost"
            circular
          />

          <!-- Custom header actions slot -->
          <slot name="header-actions" />
        </div>
      </div>

      <!-- Content area -->
      <div class="flex-1 overflow-y-auto bg-white">
        <!-- Empty state -->
        <div
          v-if="items.length === 0"
          class="h-full flex flex-col items-center justify-center text-gray-400 gap-4 p-8"
        >
          <div class="bg-primary-50 p-6 rounded-full">
            <ShoppingBag :size="48" class="opacity-50 text-primary-600" />
          </div>
          <p class="font-medium text-lg">Your cart is empty</p>
          <slot name="empty-action">
            <Button
              @click="$emit('close')"
              label="Start Ordering"
              variant="ghost"
              custom-class="mt-4 bg-white border border-gray-200 px-6 py-2 rounded-full text-gray-900 font-bold hover:bg-gray-50 transition-colors"
              size="md"
            />
          </slot>
        </div>

        <!-- Items list -->
        <div v-else class="p-5 space-y-6">
          <!-- Sent items section -->
          <div v-if="sentItems.length > 0" class="space-y-4">
            <h3
              class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3"
            >
              Sent Items
            </h3>
            <TableCartItem
              v-for="item in sentItems"
              :key="item.cartId"
              :item="item"
              :currency="currency"
              :is-disabled="true"
            />
          </div>

          <!-- New items section -->
          <div
            v-if="newItems.length > 0"
            class="space-y-4"
            :class="{
              'mt-8 pt-6 border-t border-gray-200': sentItems.length > 0
            }"
          >
            <h3
              class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3"
            >
              New Items
            </h3>
            <TableCartItem
              v-for="item in newItems"
              :key="item.cartId"
              :item="item"
              :currency="currency"
              :is-disabled="false"
              @update-quantity="onUpdateQuantity"
              @remove-item="$emit('remove-item', $event)"
              @edit-item="$emit('edit-item', $event)"
            />
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        v-if="items.length > 0"
        class="p-6 bg-white border-t border-gray-100 z-10"
        style="box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.05);"
      >
        <div class="space-y-2 mb-4 text-sm">
          <div class="flex justify-between text-gray-500">
            <span>Subtotal</span>
            <span>{{ formatPrice(subtotal) }}</span>
          </div>
          <div class="flex justify-between text-gray-500">
            <span>Tax</span>
            <span>{{ formatPrice(totalTax) }}</span>
          </div>
          <div
            v-if="grandTotal < minimumOrderRequired && minimumOrderRequired"
            class="flex justify-between text-red-500 text-xs pt-2"
          >
            <span>Min order allowed</span>
            <span>{{ formatPrice(minimumOrderRequired) }}</span>
          </div>
          <div
            class="flex justify-between font-bold text-xl text-gray-900 pt-3 border-t border-gray-100"
          >
            <span>Total</span>
            <span>{{ formatPrice(grandTotal) }}</span>
          </div>
        </div>

        <!-- Checkout Button -->
        <Button
          v-if="!isCheckoutActive && newItems.length > 0"
          @click="$emit('checkout')"
          :disabled="isMinimumOrderNotMet || isStoreClosed"
          :icon="ArrowRight"
          :icon-size="20"
          icon-position="right"
          variant="primary"
          custom-class="w-full font-bold py-4 rounded-xl shadow-lg hover:bg-primary-700 transition-all flex justify-between px-6 items-center checkout-button disabled:opacity-50 disabled:cursor-not-allowed"
          size="lg"
        >
          <span v-if="isStoreClosed">Store is Closed</span>
          <span v-else>Send Order</span>
        </Button>

        <!-- View Check Button (when there are only sent items) -->
        <Button
          v-if="
            !isCheckoutActive && newItems.length === 0 && sentItems.length > 0
          "
          @click="$emit('view-check')"
          variant="primary"
          custom-class="w-full font-bold py-4 rounded-xl shadow-lg hover:bg-primary-700 transition-all flex justify-center items-center checkout-button"
          size="lg"
        >
          View Check
        </Button>

        <div
          v-if="isCheckoutActive"
          class="w-full bg-gray-100 text-gray-400 font-bold py-4 rounded-xl flex justify-center items-center text-sm"
        >
          Checkout in progress
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Button } from "@/components/ui";
import TableCartItem from "./TableCartItem.vue";
import { Pin, PinOff, ArrowRight, X, ShoppingBag } from "lucide-vue";
import { calculateGrandTotal } from "@/helpers/orderHelpers";

export default {
  name: "TableCartBase",
  components: {
    Button,
    TableCartItem
  },
  props: {
    items: {
      type: Array,
      default: () => []
    },

    containerClasses: {
      type: [String, Array],
      required: true
    },
    containerStyle: {
      type: String,
      default: ""
    },
    showPinButton: {
      type: Boolean,
      default: false
    },
    pinButtonType: {
      type: String,
      default: "pin",
      validator: (value) => ["pin", "unpin"].includes(value)
    },
    showCloseButton: {
      type: Boolean,
      default: false
    },
    isCheckoutActive: {
      type: Boolean,
      default: false
    },
    isStoreClosed: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    "update-quantity",
    "remove-item",
    "edit-item",
    "checkout",
    "view-check",
    "close",
    "pin",
    "unpin"
  ],
  data() {
    return {
      Pin,
      PinOff,
      ArrowRight,
      X,
      ShoppingBag
    };
  },
  computed: {
    // Separate items into sent and new items
    sentItems() {
      return this.items.filter((item) => item.isSent === true);
    },
    newItems() {
      return this.items.filter((item) => item.isSent !== true);
    },
    subtotal() {
      return this.$store.getters["tableOrder/subtotal"] || 0;
    },
    totalTax() {
      return this.$store.getters["tableOrder/getTotalTax"] || 0;
    },
    grandTotal() {
      return calculateGrandTotal(
        this.subtotal,
        this.totalTax,
        this.isStoreTaxInclusive
      );
    },
    diningOptionObject() {
      return this.$store.getters.getDiningOptionObject;
    },
    minimumOrderRequired() {
      return this.diningOptionObject?.minimum_order_requirements || 0;
    },
    isMinimumOrderNotMet() {
      return (
        this.minimumOrderRequired > 0 &&
        this.grandTotal < this.minimumOrderRequired
      );
    },
    currency() {
      return this.$store.getters.getCurrencySymbol || "$";
    },
    formatPrice() {
      return this.$store.getters.formatPrice;
    },
    closeButtonClass() {
      return "p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500";
    },
    isStoreTaxInclusive() {
      const store = this.$store.getters.getStore;
      return (
        store?.tax_option === "inclusive" ||
        this.$store.getters.isStoreTaxInclusive === true
      );
    }
  },
  methods: {
    handlePinAction() {
      if (this.pinButtonType === "pin") {
        this.$emit("pin");
      } else {
        this.$emit("unpin");
      }
    },
    onUpdateQuantity(cartId, delta) {
      this.$emit("update-quantity", cartId, delta);
    }
  }
};
</script>

<style scoped>
.checkout-button:active {
  transform: scale(0.98);
}
</style>
